import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { Outlet, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Package, ShoppingCart, LogOut, ChevronRight, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";
import { BRAND } from "@/constants/brand";
import { CATEGORIES } from "@/constants/brand";

const RetailerCtx = createContext(null);

function RetailerProvider({ children }) {
  const [retailer, setRetailer] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchMe = useCallback(async () => {
    const t = localStorage.getItem("pp_retailer_token");
    if (!t) { setRetailer(false); setLoading(false); return; }
    try {
      const orig = api.defaults.headers.common.Authorization;
      const { data } = await api.get("/retailer/me", { headers: { Authorization: `Bearer ${t}` } });
      setRetailer(data);
    } catch { localStorage.removeItem("pp_retailer_token"); setRetailer(false); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    try {
      // Use a one-off request without admin token
      const res = await fetch(`${api.defaults.baseURL}/retailer/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) return { ok: false, error: data.detail || "Login failed" };
      localStorage.setItem("pp_retailer_token", data.token);
      setRetailer(data.retailer);
      return { ok: true };
    } catch (e) { return { ok: false, error: e.message }; }
  };

  const logout = () => { localStorage.removeItem("pp_retailer_token"); setRetailer(false); };

  return <RetailerCtx.Provider value={{ retailer, loading, login, logout, refresh: fetchMe }}>{children}</RetailerCtx.Provider>;
}
const useRetailer = () => useContext(RetailerCtx);

// Use bearer token override via fetch since axios shares interceptor with admin
async function retailerFetch(path, opts = {}) {
  const t = localStorage.getItem("pp_retailer_token");
  const res = await fetch(`${api.defaults.baseURL}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}`, ...(opts.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Request failed");
  return data;
}

function LoginSignup() {
  const { login, refresh } = useRetailer();
  const [mode, setMode] = useState("login");
  const [signup, setSignup] = useState({ business_name: "", contact_name: "", email: "", phone: "", city: "", state: "Karnataka", gst_number: "", password: "" });
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault(); setBusy(true);
    const r = await login(creds.email, creds.password); setBusy(false);
    if (r.ok) toast.success("Welcome back."); else toast.error(r.error);
  };
  const doSignup = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const res = await fetch(`${api.defaults.baseURL}/retailer/signup`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(signup) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Signup failed");
      toast.success("Signup received. We'll review and approve shortly.");
      setMode("login"); setCreds({ email: signup.email, password: "" });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div data-testid="retailer-auth" className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-brand-charcoal text-brand-cream p-12">
        <Link to="/" className="flex items-center gap-3" data-testid="ret-home">
          <img src={BRAND.logoUrl} alt="Pop Panda" className="h-10 w-10 bg-brand-cream rounded-full p-1.5" />
          <div className="font-display text-2xl">Pop Panda</div>
        </Link>
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-yellow">Retailer Portal</div>
          <h2 className="font-display text-5xl mt-3 leading-none">For shops, canteens,<br />school stores and more.</h2>
          <p className="mt-5 text-brand-cream/70 max-w-md">Place repeat orders, download invoices, browse promotions and chat with our support team — all in one portal.</p>
        </div>
        <div className="text-xs text-brand-cream/40">© {new Date().getFullYear()} The Pop Panda</div>
      </div>

      <div className="grid place-items-center p-8">
        <div className="w-full max-w-md">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">{mode === "login" ? "Sign in" : "Create your retailer account"}</div>
          <h1 className="editorial-h1 text-4xl mt-2">{mode === "login" ? "Welcome back" : "Become a Pop Panda retailer"}</h1>
          <div className="mt-4 flex gap-2">
            <button data-testid="ret-tab-login" onClick={() => setMode("login")} className={`btn-pill text-xs ${mode === "login" ? "btn-dark" : "btn-ghost border border-brand-line"}`}>Sign in</button>
            <button data-testid="ret-tab-signup" onClick={() => setMode("signup")} className={`btn-pill text-xs ${mode === "signup" ? "btn-dark" : "btn-ghost border border-brand-line"}`}>New here? Sign up</button>
          </div>

          {mode === "login" ? (
            <form className="mt-6 space-y-5" onSubmit={doLogin} data-testid="ret-login-form">
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Email</label><input data-testid="ret-l-email" type="email" required className="pp-input" value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Password</label><input data-testid="ret-l-pwd" type="password" required className="pp-input" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} /></div>
              <button data-testid="ret-l-submit" disabled={busy} className="btn-pill btn-dark w-full">Sign in</button>
            </form>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={doSignup} data-testid="ret-signup-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Business Name</label><input data-testid="ret-s-biz" required className="pp-input" value={signup.business_name} onChange={(e) => setSignup({ ...signup, business_name: e.target.value })} /></div>
                <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Contact Name</label><input data-testid="ret-s-name" required className="pp-input" value={signup.contact_name} onChange={(e) => setSignup({ ...signup, contact_name: e.target.value })} /></div>
                <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Email</label><input data-testid="ret-s-email" type="email" required className="pp-input" value={signup.email} onChange={(e) => setSignup({ ...signup, email: e.target.value })} /></div>
                <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Phone</label><input data-testid="ret-s-phone" required className="pp-input" value={signup.phone} onChange={(e) => setSignup({ ...signup, phone: e.target.value })} /></div>
                <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">City</label><input data-testid="ret-s-city" required className="pp-input" value={signup.city} onChange={(e) => setSignup({ ...signup, city: e.target.value })} /></div>
                <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">GST (optional)</label><input data-testid="ret-s-gst" className="pp-input" value={signup.gst_number} onChange={(e) => setSignup({ ...signup, gst_number: e.target.value })} /></div>
                <div className="sm:col-span-2"><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Password (min 6)</label><input data-testid="ret-s-pwd" type="password" required minLength={6} className="pp-input" value={signup.password} onChange={(e) => setSignup({ ...signup, password: e.target.value })} /></div>
              </div>
              <button data-testid="ret-s-submit" disabled={busy} className="btn-pill btn-dark w-full">Create account</button>
              <p className="text-xs text-brand-charcoal/55">Your account will be pending until our team approves. We'll notify you on the registered email.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function PortalShell() {
  const { retailer, logout } = useRetailer();
  const nav = useNavigate();
  const loc = useLocation();

  const tabs = [
    { to: "/retailer/portal", label: "Overview", icon: Package, end: true },
    { to: "/retailer/portal/orders", label: "Orders", icon: ShoppingCart },
    { to: "/retailer/portal/promotions", label: "Promotions", icon: ChevronRight },
  ];

  return (
    <div data-testid="retailer-portal" className="min-h-screen bg-[#FFF8F0]">
      <header className="border-b border-brand-line bg-white">
        <div className="container-pp px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={BRAND.logoUrl} className="h-8 w-8" alt="Pop Panda" />
            <div className="font-display text-xl leading-none">Pop Panda · Retailer</div>
          </Link>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] uppercase tracking-[0.22em] rounded-full px-2 py-0.5 border ${retailer?.status === "approved" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : retailer?.status === "blocked" ? "border-brand-red/30 bg-brand-red/10 text-brand-red" : "border-amber-300 bg-amber-50 text-amber-700"}`}>{retailer?.status}</span>
            <button onClick={() => { logout(); nav("/retailer"); }} data-testid="ret-logout" className="btn-pill btn-ghost text-xs"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="container-pp px-6 py-10 grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="space-y-1">
          {tabs.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={`ret-nav-${t.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-xl text-sm ${isActive ? "bg-brand-charcoal text-brand-cream" : "text-brand-charcoal/70 hover:bg-brand-muted"}`}>
              <t.icon size={16} /> {t.label}
            </NavLink>
          ))}
        </aside>
        <main><Outlet /></main>
      </div>
    </div>
  );
}

function Overview() {
  const { retailer } = useRetailer();
  const [orders, setOrders] = useState([]);
  useEffect(() => { retailerFetch("/retailer/orders").then(setOrders).catch(() => {}); }, []);
  const totalUnits = orders.reduce((a, o) => a + (o.total_units || 0), 0);
  const lastOrder = orders[0];

  return (
    <div data-testid="ret-overview" className="space-y-10">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-brand-charcoal text-brand-cream p-10 lg:p-14">
        <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-brand-yellow/20 blur-3xl" />
        <div className="absolute -left-32 -bottom-32 w-80 h-80 rounded-full bg-brand-red/20 blur-3xl" />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-yellow">Welcome back, {retailer?.contact_name?.split(" ")[0]}</div>
          <h1 className="editorial-h1 text-4xl md:text-6xl mt-3 leading-[1.05]">{retailer?.business_name}</h1>
          <p className="mt-3 text-brand-cream/65 max-w-md">{retailer?.city}, {retailer?.state || "Karnataka"}</p>
        </div>
      </div>

      {retailer?.status === "pending" && (
        <div className="card-elevated p-8 border-l-4 border-l-amber-400 bg-amber-50/50">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-700">Account pending</div>
          <div className="font-display text-2xl mt-2">Your account is awaiting approval.</div>
          <p className="text-sm text-brand-charcoal/70 mt-2">You can browse the portal but order placement is disabled until our team reviews. We typically respond within 48 hours.</p>
        </div>
      )}

      {/* KPIs */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-elevated p-6 relative overflow-hidden">
          <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-brand-yellow/20" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Lifetime Orders</div>
            <div className="font-display text-5xl mt-3">{orders.length}</div>
          </div>
        </div>
        <div className="card-elevated p-6 relative overflow-hidden">
          <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-brand-red/15" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Lifetime Units</div>
            <div className="font-display text-5xl mt-3">{totalUnits}</div>
          </div>
        </div>
        <div className="card-elevated p-6 relative overflow-hidden">
          <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-emerald-400/20" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Last order</div>
            <div className="font-display text-2xl mt-3">{lastOrder ? (lastOrder.created_at || "").slice(0, 10) : "No orders yet"}</div>
            {lastOrder && <div className="text-xs text-brand-charcoal/55 mt-1 uppercase tracking-[0.18em]">{lastOrder.status}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Orders() {
  const { retailer } = useRetailer();
  const [orders, setOrders] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [items, setItems] = useState([{ category: "water", quantity: 50, notes: "" }]);
  const [meta, setMeta] = useState({ delivery_address: "", expected_date: "", notes: "" });

  const load = () => retailerFetch("/retailer/orders").then(setOrders).catch(() => {});
  useEffect(() => { load(); }, []);

  const place = async (e) => {
    e.preventDefault();
    try {
      await retailerFetch("/retailer/orders", { method: "POST", body: JSON.stringify({ items, ...meta }) });
      toast.success("Order placed.");
      setShowNew(false); setItems([{ category: "water", quantity: 50, notes: "" }]); load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="ret-orders" className="space-y-6">
      <div className="flex items-end justify-between"><div><div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Orders</div><h1 className="editorial-h1 text-4xl mt-2">Order history</h1></div>
        <button data-testid="ret-new-order" disabled={retailer?.status !== "approved"} onClick={() => setShowNew(true)} className="btn-pill btn-dark text-sm disabled:opacity-40"><Plus size={14} /> Place order</button>
      </div>

      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">
            <tr className="border-b border-brand-line"><th className="p-4">When</th><th className="p-4">Items</th><th className="p-4">Units</th><th className="p-4">Status</th></tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-brand-line last:border-0">
                <td className="p-4 text-xs text-brand-charcoal/65">{(o.created_at || "").slice(0, 10)}</td>
                <td className="p-4">{o.items.map((i) => `${i.category}×${i.quantity}`).join(", ")}</td>
                <td className="p-4">{o.total_units}</td>
                <td className="p-4 text-xs uppercase tracking-[0.18em] text-brand-charcoal/65">{o.status}</td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-brand-charcoal/60">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {showNew && (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/40 grid place-items-center p-4">
          <form className="bg-white rounded-3xl w-full max-w-xl p-8 space-y-4" onSubmit={place} data-testid="ret-order-form">
            <div className="font-display text-2xl">New order</div>
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5"><label className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Category</label>
                  <select className="pp-input bg-transparent" value={it.category} onChange={(e) => { const arr=[...items]; arr[i]={...it,category:e.target.value}; setItems(arr); }}>
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div className="col-span-3"><label className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Qty</label><input type="number" min="1" className="pp-input" value={it.quantity} onChange={(e) => { const arr=[...items]; arr[i]={...it,quantity:Number(e.target.value)||0}; setItems(arr); }} /></div>
                <div className="col-span-3"><label className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Notes</label><input className="pp-input" value={it.notes} onChange={(e) => { const arr=[...items]; arr[i]={...it,notes:e.target.value}; setItems(arr); }} /></div>
                <div className="col-span-1"><button type="button" onClick={() => setItems(items.filter((_,j)=>j!==i))} className="text-brand-red"><Trash2 size={14} /></button></div>
              </div>
            ))}
            <button type="button" onClick={() => setItems([...items, { category: "water", quantity: 50, notes: "" }])} className="btn-pill btn-ghost text-xs border border-brand-line"><Plus size={12} /> Add item</button>
            <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Delivery address</label><textarea required rows="2" className="pp-input" value={meta.delivery_address} onChange={(e) => setMeta({...meta, delivery_address: e.target.value})} /></div>
            <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Expected date</label><input type="date" className="pp-input" value={meta.expected_date} onChange={(e) => setMeta({...meta, expected_date: e.target.value})} /></div>
            <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Notes</label><textarea rows="2" className="pp-input" value={meta.notes} onChange={(e) => setMeta({...meta, notes: e.target.value})} /></div>
            <div className="flex justify-end gap-3"><button type="button" onClick={() => setShowNew(false)} className="btn-pill btn-ghost text-sm">Cancel</button><button type="submit" className="btn-pill btn-dark text-sm">Place order</button></div>
          </form>
        </div>
      )}
    </div>
  );
}

function Promotions() {
  const [promos, setPromos] = useState([]);
  useEffect(() => { retailerFetch("/retailer/promotions").then(setPromos).catch(() => setPromos([])); }, []);
  return (
    <div data-testid="ret-promotions" className="space-y-6">
      <div><div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Promotions</div><h1 className="editorial-h1 text-4xl mt-2">Active offers for retailers</h1></div>
      <div className="card-elevated p-10 text-center text-brand-charcoal/60">No active promotions yet — our team will post seasonal offers here.</div>
    </div>
  );
}

function PortalGuard({ children }) {
  const { retailer, loading } = useRetailer();
  if (loading || retailer === null) return <div className="min-h-screen grid place-items-center text-brand-charcoal/60">Loading…</div>;
  if (!retailer) return <LoginSignup />;
  return children;
}

export default function RetailerPortal({ section = "overview" }) {
  return (
    <RetailerProvider>
      <PortalGuard>
        <PortalShell />
      </PortalGuard>
    </RetailerProvider>
  );
}

// Sub-routes — exported so App.js can mount them inside RetailerPortal
RetailerPortal.Overview = Overview;
RetailerPortal.Orders = Orders;
RetailerPortal.Promotions = Promotions;
